import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  useMemo,
  useState,
} from "react";
import {
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
  TextField,
} from "@mui/material";
import { Search } from "@mui/icons-material";

// Options past this count get a search box; below it, this is a byte-for-byte
// passthrough to MUI's own Select with no behavior change.
const SEARCH_THRESHOLD = 5;

const getItemText = (child) => {
  const { children } = child.props;
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children
      .filter((c) => typeof c === "string" || typeof c === "number")
      .join(" ");
  }
  return "";
};

// Drop-in replacement for MUI's <Select>: identical everywhere below
// searchThreshold options, and only above it gains a search box pinned to
// the top of the menu. Matched-out MenuItems stay mounted (display: none)
// rather than unmounting, since Select resolves its own displayed value by
// walking children for a matching `value` prop — removing the selected
// item from the tree while it's hidden would break that lookup.
const SearchableSelect = ({
  children,
  onClose,
  MenuProps,
  searchThreshold = SEARCH_THRESHOLD,
  searchPlaceholder = "Search…",
  ...rest
}) => {
  const [search, setSearch] = useState("");
  const searchId = useId();

  const items = Children.toArray(children);
  const optionCount = items.filter(
    (child) => isValidElement(child) && child.type === MenuItem
  ).length;
  const searchable = optionCount > searchThreshold;

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!searchable || !query) return items;
    return items.map((child) => {
      if (!isValidElement(child) || child.type !== MenuItem) return child;
      const matches = getItemText(child).toLowerCase().includes(query);
      return matches
        ? child
        : cloneElement(child, {
            style: { ...child.props.style, display: "none" },
          });
    });
  }, [items, search, searchable]);

  if (!searchable) {
    return (
      <MuiSelect onClose={onClose} MenuProps={MenuProps} {...rest}>
        {children}
      </MuiSelect>
    );
  }

  return (
    <MuiSelect
      onClose={(event) => {
        setSearch("");
        onClose?.(event);
      }}
      MenuProps={{
        ...MenuProps,
        autoFocus: false,
        PaperProps: {
          ...MenuProps?.PaperProps,
          style: { maxHeight: 360, ...MenuProps?.PaperProps?.style },
        },
      }}
      {...rest}
    >
      <ListSubheader
        // Excluded from Select's own value-lookup and keyboard nav (it has no
        // `value` prop), so it can host the search field without interfering
        // with either.
        key={`search-${searchId}`}
        sx={{ px: 1.5, py: 1 }}
      >
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            // Stop everything but Escape from reaching Select's own listener,
            // which otherwise treats keystrokes as type-to-select and steals
            // them from the text field.
            if (event.key !== "Escape") event.stopPropagation();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </ListSubheader>
      {filteredItems}
    </MuiSelect>
  );
};

export default SearchableSelect;
